/**
 * The client half of the edge functions' list contract
 * (`supabase/functions/_shared/app/list-query.ts`). Shared by every paged
 * screen — agents today, tools / knowledge bases / phone numbers / calls next.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/** The server's ceiling on `size`. */
export const MAX_PAGE_SIZE = 100;

/** Matches the admin portal's debounce, so both portals feel the same. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * No sort: every list is served in its module's own order — most recently
 * edited first — because no screen offers a sort control, and each column a
 * caller could sort by is an index the table has to carry.
 */
export interface ListParams {
  page: number;
  size: number;
  search: string;
}

export interface PagedResult<T> {
  rows: T[];
  /** The whole set, not this page — what a pager needs to size itself. */
  total: number;
  page: number;
}

/** What a dropdown renders. Modules map their own row onto it. */
export interface OptionItem {
  id: string;
  name: string;
}

/** A list is a POST, so the params travel as a document. `undefined` keys drop
 * out of `JSON.stringify`, which is what lets the server apply its defaults. */
export function listParamsToBody(params: ListParams): Record<string, unknown> {
  return {
    page: params.page,
    size: params.size,
    search: params.search || undefined,
  };
}

export const emptyPage = <T>(): PagedResult<T> => ({ rows: [], total: 0, page: 1 });

export function totalPages(total: number, size: number): number {
  return Math.max(1, Math.ceil(total / size));
}

export interface ListParamsController {
  params: ListParams;
  /** The raw input value — `params.search` lags it by the debounce. */
  searchInput: string;
  setSearchInput: (value: string) => void;
  setPage: (page: number) => void;
  setSize: (size: number) => void;
  /**
   * Pull the page back into range once the server reports a smaller total.
   * Deleting the last row on the last page otherwise strands the screen on a
   * page that no longer exists — an empty list under a pager reading "Page 2
   * of 1".
   */
  clampToTotal: (total: number) => void;
}

export function useListParams(initial: Partial<ListParams> = {}): ListParamsController {
  const [params, setParams] = useState<ListParams>({
    page: 1,
    size: DEFAULT_PAGE_SIZE,
    search: "",
    ...initial,
  });
  const [searchInput, setSearchInput] = useState(params.search);

  useEffect(() => {
    if (searchInput === params.search) return;
    const timer = setTimeout(() => {
      // A new search invalidates the current page: without this, searching from
      // page 3 requests a page the filtered result may not have.
      setParams((current) => ({ ...current, search: searchInput, page: 1 }));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, params.search]);

  const setPage = useCallback((page: number) => {
    setParams((current) => ({ ...current, page }));
  }, []);

  const setSize = useCallback((size: number) => {
    setParams((current) => ({ ...current, size, page: 1 }));
  }, []);

  const clampToTotal = useCallback((total: number) => {
    setParams((current) => {
      const last = totalPages(total, current.size);
      return current.page <= last ? current : { ...current, page: last };
    });
  }, []);

  return useMemo(
    () => ({ params, searchInput, setSearchInput, setPage, setSize, clampToTotal }),
    [params, searchInput, setPage, setSize, clampToTotal],
  );
}
